import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, AlertTriangle, CheckCircle, XCircle, Download, FileText } from "lucide-react";

interface CSPDirective {
  directive: string;
  value: string;
  severity: 'safe' | 'warning' | 'danger';
  recommendation?: string;
}

interface Scan {
  id: number;
  url: string;
  status: string;
  results?: string;
  createdAt: string;
  updatedAt: string;
}

interface CSPAnalysis {
  scanId: number;
  url: string;
  hasCSP: boolean;
  cspHeader?: string;
  cspSource?: string;
  liveVerified?: boolean;
  presentSecurityHeaders?: string[];
  note?: string;
  directives: CSPDirective[];
  issues: {
    type: string;
    severity: 'low' | 'medium' | 'high';
    description: string;
    recommendation: string;
  }[];
  score: number;
  improvements: string[];
}

export function CSPAnalysis() {
  // Get the latest scan to analyze CSP
  const { data: scans } = useQuery<Scan[]>({
    queryKey: ["/api/scans"],
  });

  const latestScan = scans && scans.length > 0 ? scans[0] : null;

  const { data: cspData, isLoading } = useQuery<CSPAnalysis>({
    queryKey: ["/api/scans", latestScan?.id, "csp"],
    enabled: !!latestScan?.id, // Only enabled when we have a real scan
  });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'safe': return "text-purple-600 dark:text-purple-400";
      case 'warning': return "text-yellow-600 dark:text-yellow-400";
      case 'danger': return "text-red-600 dark:text-red-400";
      case 'high': return "text-red-600 dark:text-red-400";
      case 'medium': return "text-yellow-600 dark:text-yellow-400";
      case 'low': return "text-blue-600 dark:text-blue-400";
      default: return "text-gray-600 dark:text-gray-400";
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'safe': return <CheckCircle className="h-4 w-4 text-purple-600" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'danger': return <XCircle className="h-4 w-4 text-red-600" />;
      case 'high': return <XCircle className="h-4 w-4 text-red-600" />;
      case 'medium': return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'low': return <CheckCircle className="h-4 w-4 text-blue-600" />;
      default: return <AlertTriangle className="h-4 w-4 text-gray-600" />;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            CSP Header Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!cspData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            CSP Header Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 dark:text-gray-400">
            No CSP analysis available. Run a scan to analyze Content Security Policy headers.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            CSP Header Analysis - {cspData.url}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Content Security Policy configuration and recommendations
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => {
            if (cspData?.scanId) {
              window.open(`/api/export/csp/${cspData.scanId}`, '_blank');
            } else if (latestScan?.id) {
              window.open(`/api/export/csp/${latestScan.id}`, '_blank');
            }
          }}
        >
          <Download className="h-4 w-4 mr-2" />
          Export CSP Report
        </Button>
      </div>

      {cspData.liveVerified && (
        <Alert>
          <AlertDescription>
            {cspData.hasCSP
              ? `Live HTTP check detected CSP${cspData.cspSource ? ` (${cspData.cspSource})` : ''}.`
              : cspData.note || 'Live HTTP check confirmed no Content-Security-Policy header on this URL.'}
            {cspData.presentSecurityHeaders && cspData.presentSecurityHeaders.length > 0 && (
              <span className="block mt-1 text-sm">
                Headers found: {cspData.presentSecurityHeaders.join(', ')}
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              {cspData.hasCSP ? (
                <CheckCircle className="h-5 w-5 text-purple-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
              <div>
                <div className="font-medium">
                  {cspData.hasCSP ? 'CSP Present' : 'No CSP'}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {cspData.hasCSP ? 'Header detected' : 'Missing CSP header'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {cspData.score}/100
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Security Score</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {cspData.issues.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Issues Found</div>
          </CardContent>
        </Card>
      </div>

      {/* Current CSP Header */}
      {cspData.hasCSP && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Current CSP Header
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
              <code className="text-sm text-gray-800 dark:text-gray-200 break-all">
                {cspData.cspHeader}
              </code>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Directive Analysis */}
      {cspData.directives.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>CSP Directives Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {cspData.directives.map((directive, index) => (
                <div
                  key={index}
                  className="flex items-start justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge
                        variant={directive.severity === 'safe' ? 'default' : 
                                directive.severity === 'warning' ? 'secondary' : 'destructive'}
                      >
                        {directive.directive}
                      </Badge>
                      {getSeverityIcon(directive.severity)}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span className="font-medium">Value:</span> {directive.value}
                    </div>
                    {directive.recommendation && (
                      <div className="text-sm text-blue-600 dark:text-blue-400">
                        <span className="font-medium">Recommendation:</span> {directive.recommendation}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Security Issues */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Security Issues & Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {cspData.issues.map((issue, index) => (
              <Alert key={index} className="border-l-4" style={{
                borderLeftColor: issue.severity === 'high' ? '#ef4444' : 
                                issue.severity === 'medium' ? '#f59e0b' : '#3b82f6'
              }}>
                <div className="flex items-start gap-3">
                  {getSeverityIcon(issue.severity)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{issue.type}</span>
                      <Badge variant={issue.severity === 'high' ? 'destructive' : 
                                    issue.severity === 'medium' ? 'secondary' : 'outline'}>
                        {issue.severity.toUpperCase()}
                      </Badge>
                    </div>
                    <AlertDescription className="mb-2">
                      {issue.description}
                    </AlertDescription>
                    <div className="text-sm text-purple-600 dark:text-purple-400">
                      <span className="font-medium">Recommendation:</span> {issue.recommendation}
                    </div>
                  </div>
                </div>
              </Alert>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Improvement Suggestions */}
      <Card>
        <CardHeader>
          <CardTitle>Recommended CSP Improvements</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {cspData.improvements.map((improvement, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-purple-600 mt-0.5" />
                <p className="text-sm text-gray-700 dark:text-gray-300">{improvement}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}